import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import DateTimePicker from './DateTimePicker';
import {
  TrashIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  DocumentIcon,
  FolderIcon,
  EyeIcon,
  ArrowPathIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  CameraIcon,
  PrinterIcon,
  FolderPlusIcon,
  DocumentDuplicateIcon,
  PlusCircleIcon,
  ArrowRightCircleIcon,
  BuildingOfficeIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { apiService, Policy, CreatePolicyRequest, FilePermissions, FolderPermissions as ApiFolderPermissions, RecipientTarget } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PolicyRecipientSelector from './PolicyRecipientSelector';
import { useCompanyPolicySettings } from '../hooks/useCompanyPolicySettings';

interface PolicyCreateFormProps {
  onClose: () => void;
  editingPolicy?: Policy | null;
  templatePolicy?: Policy | null;
}

const PolicyCreateForm: React.FC<PolicyCreateFormProps> = ({ onClose, editingPolicy, templatePolicy }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isFilePermissionVisible, isFolderPermissionVisible } = useCompanyPolicySettings();
  const [step, setStep] = useState(1);
  
  // Policy recipient management
  const [policyRecipients, setPolicyRecipients] = useState<RecipientTarget[]>([]);

  // Access limitations toggle state
  const [accessCountEnabled, setAccessCountEnabled] = useState(false);
  const [accessLimitationsEnabled, setAccessLimitationsEnabled] = useState(false); // Time-based limitations toggle
  const [timeLimitationType, setTimeLimitationType] = useState<'days' | 'datetime'>('days');
  const [daysValue, setDaysValue] = useState<number>(1);
  const [daysUnit, setDaysUnit] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [datetimeErrors, setDatetimeErrors] = useState<{
    from?: string;
    to?: string;
  }>({});

  // Policy form state
  const [formData, setFormData] = useState<CreatePolicyRequest>({
    name: '',
    description: '',
    type: undefined,
    asset_type: undefined,
    settings: {
      encryption_type: 'AES-256',
      recipient_settings: {
        allowed_domains: [],
        allowed_subdomains: [],
        require_approval: false,
      },
      file_permissions: {
        convert_original: { allowed: false, audit_level: 'basic' },
        save_changes: { allowed: false, audit_level: 'basic' },
        copy_paste: { allowed: false, audit_level: 'basic' },
        screen_capture: { allowed: false, audit_level: 'basic' },
        print: { allowed: false, audit_level: 'basic' },
        share: { allowed: false, audit_level: 'basic' },
      },
      folder_permissions: {
        create: { allowed: false, audit_level: 'basic' },
        copy: { allowed: false, audit_level: 'basic' },
        add: { allowed: false, audit_level: 'basic' },
        move: { allowed: false, audit_level: 'basic' },
        share: { allowed: false, audit_level: 'basic' },
        delete: { allowed: false, audit_level: 'basic' },
      },
      screen_watermark: false,
      print_watermark: false,
      security: {
        auth_on_asset_open: false,
        authentication: {
          two_factor: false,
          biometric: false,
          max_attempts: 3,
        },
        session: {
          timeout_seconds: 1800,
          allow_refresh: true,
        },
        protection: {
          delete_on_unauthorized: false,
          delete_on_limit_exceeded: false,
        },
      },
      access_limitations: {
        access_count_limitation: undefined,
        allowed_access_from: undefined,
        allowed_access_to: undefined,
      },
      recipient_message: '',
    },
  });

  // Role-based restrictions.
  // Personal (user) policies are open to everyone with an account —
  // sysadmins, company admins, and end-users alike. The earlier
  // admin-only-company-policy gate left admins with no way to
  // provision personal policies for themselves even though they had
  // personal auto-encrypt folders that needed them. Backend already
  // permits this path (`canCreatePolicyType` in role_middleware.go).
  const canCreateSystemPolicies = user?.role === 'sysadmin';
  const canCreateCompanyPolicies = user?.role === 'sysadmin' || user?.role === 'admin';
  const canCreateUserPolicies = !!user;

  // Available policy types based on user role
  const availablePolicyTypes = [
    ...(canCreateSystemPolicies ? [{ value: 'system', label: 'System Policy', description: 'Global system-wide policy' }] : []),
    ...(canCreateCompanyPolicies ? [{ value: 'company', label: 'Company Policy', description: 'Company-wide policy' }] : []),
    ...(canCreateUserPolicies ? [{ value: 'user', label: 'Personal Policy', description: 'Your personal policy' }] : []),
  ];

  // Auto-set policy type for user role
  useEffect(() => {
    if (user?.role === 'user') {
      setFormData(prev => ({ ...prev, type: 'user' }));
    }
  }, [user?.role]);

  // Initialize form data if editing or templating
  useEffect(() => {
    if (editingPolicy) {
      setFormData({
        name: editingPolicy.name,
        description: editingPolicy.description,
        type: editingPolicy.type as any,
        asset_type: editingPolicy.asset_type,
        settings: editingPolicy.settings,
      });
    } else if (templatePolicy) {
      // Initialize from template with modified name
      // Always set type to 'user' for regular users, regardless of template type
      const templateSettings = { ...templatePolicy.settings };
      // Handle backward compatibility - convert old watermark objects to booleans
      if (typeof templateSettings.screen_watermark !== 'boolean') {
        templateSettings.screen_watermark = (templateSettings.screen_watermark as any)?.enabled ?? false;
      }
      if (typeof templateSettings.print_watermark !== 'boolean') {
        templateSettings.print_watermark = (templateSettings.print_watermark as any)?.enabled ?? false;
      }
      setFormData({
        name: `${templatePolicy.name} (Copy)`,
        description: templatePolicy.description,
        type: 'user', // Force user type for regular users
        asset_type: templatePolicy.asset_type,
        settings: templateSettings,
      });
    }
  }, [editingPolicy, templatePolicy]);

  // Create/Update policy mutation
  const savePolicyMutation = useMutation({
    mutationFn: async (policyData: CreatePolicyRequest) => {
      if (editingPolicy) {
        return apiService.updatePolicy(editingPolicy.id, policyData as any);
      } else {
        return apiService.createPolicy(policyData);
      }
    },
    onSuccess: async (createdPolicy) => {
      // Save policy recipients if any are selected
      if (policyRecipients.length > 0 && createdPolicy?.id) {
        try {
          await apiService.addPolicyRecipients(createdPolicy.id, policyRecipients);
          console.log('✅ Policy recipients saved successfully');
        } catch (error) {
          console.error('❌ Failed to save policy recipients:', error);
          toast.error('Policy created but failed to save recipients');
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      toast.success(editingPolicy ? 'Policy updated successfully' : 'Policy created successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save policy');
    },
  });

  // Create draft policy mutation
  const saveDraftMutation = useMutation({
    mutationFn: async (policyData: CreatePolicyRequest) => {
      if (editingPolicy) {
        return apiService.updatePolicy(editingPolicy.id, { ...policyData, status: 'draft' } as any);
      } else {
        return apiService.createPolicy({ ...policyData, status: 'draft' });
      }
    },
    onSuccess: async (createdPolicy) => {
      // Save policy recipients if any are selected
      if (policyRecipients.length > 0 && createdPolicy?.id) {
        try {
          await apiService.addPolicyRecipients(createdPolicy.id, policyRecipients);
          console.log('✅ Policy recipients saved successfully');
        } catch (error) {
          console.error('❌ Failed to save policy recipients:', error);
          toast.error('Draft saved but failed to save recipients');
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      toast.success(editingPolicy ? 'Policy saved as draft' : 'Draft policy created successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save draft policy');
    },
  });



  // Handle time limitation type change and clear values
  const handleTimeLimitationTypeChange = (type: 'days' | 'datetime') => {
    setTimeLimitationType(type);
    // Clear the opposite values when switching
    if (type === 'days') {
      // Clear datetime values when switching to days
      setFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          }
        }
      }));
    } else {
      // Clear days value when switching to datetime
      setDaysValue(1);
      setDaysUnit('day');
      // Also clear the calculated datetime values from days
      setFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          }
        }
      }));
    }
  };

  // Calculate datetime values from days
  const calculateDateTimeFromDays = (days: number, unit: 'day' | 'week' | 'month' | 'year') => {
    const now = new Date();
    let daysToAdd = days;
    
    switch (unit) {
      case 'week':
        daysToAdd = days * 7;
        break;
      case 'month':
        daysToAdd = days * 30; // Approximate
        break;
      case 'year':
        daysToAdd = days * 365; // Approximate
        break;
      default:
        daysToAdd = days;
    }
    
    const fromDate = new Date(now);
    const toDate = new Date(now.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
    
    return {
      from: fromDate.toISOString(),
      to: toDate.toISOString()
    };
  };

  // Update datetime values when days change
  useEffect(() => {
    if (timeLimitationType === 'days' && accessLimitationsEnabled && daysValue > 0) {
      const { from, to } = calculateDateTimeFromDays(daysValue, daysUnit);
      setFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: from,
            allowed_access_to: to,
          }
        }
      }));
    } else if (timeLimitationType === 'days' && accessLimitationsEnabled && daysValue <= 0) {
      // If days value is empty or invalid, clear datetime values
      setFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          }
        }
      }));
    }
  }, [daysValue, daysUnit, timeLimitationType, accessLimitationsEnabled]);

  // Validate datetime values
  const validateDatetimeValues = (fromValue?: string, toValue?: string) => {
    const errors: { from?: string; to?: string } = {};
    const now = new Date();
    
    if (fromValue) {
      const fromDate = new Date(fromValue);
      if (fromDate < now) {
        errors.from = 'Access from time cannot be in the past';
      }
    }
    
    if (toValue) {
      const toDate = new Date(toValue);
      if (toDate < now) {
        errors.to = 'Access to time cannot be in the past';
      }
    }
    
    if (fromValue && toValue) {
      const fromDate = new Date(fromValue);
      const toDate = new Date(toValue);
      if (fromDate >= toDate) {
        errors.to = 'Access to time must be after access from time';
      }
    }
    
    setDatetimeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate form before submission
  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Policy name is required');
      return false;
    }
    
    if (!formData.type) {
      toast.error('Policy type is required');
      return false;
    }
    
    if (!formData.asset_type) {
      toast.error('Asset type is required');
      return false;
    }
    
    // Validate datetime values if access limitations are enabled and datetime type is selected
    if (accessLimitationsEnabled && timeLimitationType === 'datetime') {
      const fromValue = formData.settings.access_limitations?.allowed_access_from;
      const toValue = formData.settings.access_limitations?.allowed_access_to;
      
      if (fromValue || toValue) {
        const isValid = validateDatetimeValues(fromValue, toValue);
        if (!isValid) {
          toast.error('Please fix the datetime validation errors');
          return false;
        }
      }
    }
    
    return true;
  };

  const updatePermission = (section: string, permission: string, value: boolean | number | string | undefined) => {
    setFormData(prev => {
      const updated = { ...prev };
      
      if (section === 'file_permissions') {
        if (updated.settings.file_permissions) {
          updated.settings.file_permissions = {
            ...updated.settings.file_permissions,
            [permission]: { 
              allowed: value as boolean, 
              audit_level: updated.settings.file_permissions[permission as keyof FilePermissions]?.audit_level || 'basic' 
            },
          };
        }
      } else if (section === 'folder_permissions') {
        if (updated.settings.folder_permissions) {
          updated.settings.folder_permissions = {
            ...updated.settings.folder_permissions,
            [permission]: { 
              allowed: value as boolean, 
              audit_level: updated.settings.folder_permissions[permission as keyof ApiFolderPermissions]?.audit_level || 'basic' 
            },
          };
        }
      } else if (section === 'security') {
        if (permission === 'auth_on_asset_open') {
          updated.settings.security = {
            ...updated.settings.security,
            auth_on_asset_open: value as boolean,
          };
        } else if (permission === 'two_factor') {
          updated.settings.security.authentication = {
            two_factor: value as boolean,
            biometric: updated.settings.security.authentication?.biometric || false,
            max_attempts: updated.settings.security.authentication?.max_attempts || 3,
          };
        } else if (permission === 'biometric') {
          updated.settings.security.authentication = {
            two_factor: updated.settings.security.authentication?.two_factor || false,
            biometric: value as boolean,
            max_attempts: updated.settings.security.authentication?.max_attempts || 3,
          };
        } else if (permission === 'max_attempts') {
          updated.settings.security.authentication = {
            two_factor: updated.settings.security.authentication?.two_factor || false,
            biometric: updated.settings.security.authentication?.biometric || false,
            max_attempts: value as number,
          };
        } else if (permission === 'timeout_seconds') {
          updated.settings.security.session = {
            timeout_seconds: value as number,
            allow_refresh: updated.settings.security.session?.allow_refresh || true,
          };
        } else if (permission === 'delete_on_unauthorized') {
          updated.settings.security.protection = {
            delete_on_unauthorized: value as boolean,
            delete_on_limit_exceeded: updated.settings.security.protection?.delete_on_limit_exceeded || false,
          };
        } else if (permission === 'delete_on_limit_exceeded') {
          updated.settings.security.protection = {
            delete_on_unauthorized: updated.settings.security.protection?.delete_on_unauthorized || false,
            delete_on_limit_exceeded: value as boolean,
          };
        }
      } else if (section === 'watermark') {
        if (permission === 'screen_enabled') {
          updated.settings.screen_watermark = value as boolean;
        } else if (permission === 'print_enabled') {
          updated.settings.print_watermark = value as boolean;
        }
      } else if (section === 'general') {
        if (permission === 'access_count_limitation') {
          updated.settings.access_limitations = {
            ...updated.settings.access_limitations,
            access_count_limitation: value !== undefined ? (value as number) : undefined,
          };
        } else if (permission === 'allowed_access_from') {
          updated.settings.access_limitations = {
            ...updated.settings.access_limitations,
            allowed_access_from: value as string,
          };
        } else if (permission === 'allowed_access_to') {
          updated.settings.access_limitations = {
            ...updated.settings.access_limitations,
            allowed_access_to: value as string,
          };
        }
      }
      
      return updated;
    });
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    savePolicyMutation.mutate(formData);
  };

  const handleSaveDraft = () => {
    if (!formData.name.trim()) {
      toast.error('Policy name is required');
      return;
    }

    saveDraftMutation.mutate(formData);
  };




  // Toggle Switch Component
  const ToggleSwitch: React.FC<{
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
  }> = ({ checked, onChange, disabled = false }) => (
    <button
      type="button"
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
        checked ? 'bg-green-600' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  return (
    <div className="flex h-full bg-white">

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {step === 1 && (
            <div className="p-6">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Policy Configuration</h2>
                
                {/* Basic Information */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Policy Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      placeholder="Enter policy name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      rows={3}
                      placeholder="Enter policy description"
                    />
                  </div>

                  {/* Policy Type Selection - Only show for sysadmin and admin roles */}
                  {(user?.role === 'sysadmin' || user?.role === 'admin') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Policy Type *
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {availablePolicyTypes.map((type) => (
                          <div
                            key={type.value}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                              formData.type === type.value
                                ? 'border-governate-500 bg-governate-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setFormData(prev => ({ ...prev, type: type.value as any }))}
                          >
                            <div className="flex items-center space-x-3">
                              {type.value === 'system' && <ShieldCheckIcon className="w-6 h-6 text-blue-500" />}
                              {type.value === 'company' && <BuildingOfficeIcon className="w-6 h-6 text-green-500" />}
                              {type.value === 'user' && <UserIcon className="w-6 h-6 text-gray-500" />}
                              <div>
                                <h3 className="font-medium text-gray-900">{type.label}</h3>
                                <p className="text-sm text-gray-500">{type.description}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Show policy type info for user role */}
                  {user?.role === 'user' && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <UserIcon className="w-6 h-6 text-gray-500" />
                        <div>
                          <h3 className="font-medium text-gray-900">Personal Policy</h3>
                          <p className="text-sm text-gray-500">
                            This policy will be created for your personal use
                          </p>
                          {templatePolicy && (
                            <p className="text-xs text-blue-600 mt-1">
                              📋 Template: "{templatePolicy.name}" ({templatePolicy.asset_type} policy)
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Asset Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Asset Type *
                    </label>
                    
                    {/* Template info */}
                    {templatePolicy && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <DocumentDuplicateIcon className="w-5 h-5 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800">
                            Template: {templatePolicy.name}
                          </span>
                        </div>
                        <p className="text-sm text-blue-700 mt-1">
                          Creating from a <strong>{templatePolicy.asset_type}</strong> policy template. 
                          {templatePolicy.asset_type === 'file' 
                            ? ' This policy will apply to individual files with specific permissions like view, edit, print, and share.'
                            : ' This policy will apply to folders and all files within them with folder-level permissions.'
                          }
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        className={`p-4 border-2 rounded-lg transition-colors ${
                          formData.asset_type === 'file'
                            ? 'border-governate-500 bg-governate-50'
                            : templatePolicy 
                              ? 'border-gray-200 opacity-50 cursor-not-allowed'
                              : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                        }`}
                        onClick={() => !templatePolicy && setFormData(prev => ({ ...prev, asset_type: 'file' }))}
                      >
                        <div className="flex items-center space-x-3">
                          <DocumentIcon className="w-6 h-6 text-blue-500" />
                          <div>
                            <h3 className="font-medium text-gray-900">File Policy</h3>
                            <p className="text-sm text-gray-500">For individual files</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Controls permissions for specific files: view, edit, print, share, delete
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        className={`p-4 border-2 rounded-lg transition-colors ${
                          formData.asset_type === 'folder'
                            ? 'border-governate-500 bg-governate-50'
                            : templatePolicy 
                              ? 'border-gray-200 opacity-50 cursor-not-allowed'
                              : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                        }`}
                        onClick={() => !templatePolicy && setFormData(prev => ({ ...prev, asset_type: 'folder' }))}
                      >
                        <div className="flex items-center space-x-3">
                          <FolderIcon className="w-6 h-6 text-green-500" />
                          <div>
                            <h3 className="font-medium text-gray-900">Folder Policy</h3>
                            <p className="text-sm text-gray-500">For folders and collections</p>
                            <p className="text-xs text-gray-400 mt-1">
                              Controls permissions for entire folders: access, create, modify, organize
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Navigation */}
                <div className="flex justify-end mt-8">
                  <button
                    onClick={() => setStep(2)}
                    disabled={!formData.name.trim() || !formData.type || !formData.asset_type}
                    className="px-6 py-2 bg-governate-600 text-white rounded-lg hover:bg-governate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next: Configure Permissions
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="p-6">
              <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Configure Permissions</h2>
                  <button
                    onClick={() => setStep(1)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    ← Back to Basic Info
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Access Limitations */}
                    <div className="card">
                      <div className="flex items-center space-x-2 mb-4">
                          <ShieldCheckIcon className="w-5 h-5 text-green-600" />
                          <h3 className="text-lg font-semibold text-gray-900">Access Limitations</h3>
                      </div>
                      
                        <div className="space-y-6">
                          {/* Access Count Limitation */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <label className="block text-sm font-medium text-gray-700">
                                Access Count Limitation
                              </label>
                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newValue = !accessCountEnabled;
                                    setAccessCountEnabled(newValue);
                                    
                                    if (!newValue) {
                                      // Clear access_count_limitation when disabling
                                      setFormData(prev => ({
                                        ...prev,
                                        settings: {
                                          ...prev.settings,
                                          access_limitations: {
                                            ...prev.settings.access_limitations,
                                            access_count_limitation: undefined
                                          }
                                        }
                                      }));
                                    }
                                  }}
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                                    accessCountEnabled ? 'bg-governate-600' : 'bg-gray-200'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                      accessCountEnabled ? 'translate-x-5' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </div>
                            </div>
                            
                            {accessCountEnabled && (
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  value={formData.settings.access_limitations?.access_count_limitation ?? ''}
                                  onChange={(e) => {
                                    if (e.target.value === '') {
                                      // Empty input means unlimited - set to undefined
                                      updatePermission('general', 'access_count_limitation', undefined);
                                    } else {
                                      const value = parseInt(e.target.value);
                                      if (value && value > 0) {
                                        updatePermission('general', 'access_count_limitation', value);
                                      }
                                    }
                                  }}
                                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                                  placeholder=""
                                  min="1"
                                  step="1"
                                />
                                <span className="text-sm text-gray-500">times</span>
                              </div>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              Maximum number of times this asset can be accessed
                            </p>
                          </div>

                          {/* Time-based Limitations */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <label className="block text-sm font-medium text-gray-700">
                              Time-based Limitations
                            </label>
                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newValue = !accessLimitationsEnabled;
                                    setAccessLimitationsEnabled(newValue);
                                    
                                    // Clear time-based values when disabling
                                    if (!newValue) {
                                      setFormData(prev => ({
                                        ...prev,
                                        settings: {
                                          ...prev.settings,
                                          access_limitations: {
                                            ...prev.settings.access_limitations,
                                            allowed_access_from: undefined,
                                            allowed_access_to: undefined,
                                          }
                                        }
                                      }));
                                    }
                                  }}
                                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                                    accessLimitationsEnabled ? 'bg-governate-600' : 'bg-gray-200'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                      accessLimitationsEnabled ? 'translate-x-5' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </div>
                            </div>
                            
                            {accessLimitationsEnabled && (
                            <div className="space-y-4">
                              {/* Radio buttons - reordered */}
                              <div className="space-y-2">
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name="timeOption"
                                    value="days"
                                    checked={timeLimitationType === 'days'}
                                    onChange={() => handleTimeLimitationTypeChange('days')}
                                    className="mr-2"
                                  />
                                  <span className="text-sm text-gray-700">Number of days from creation</span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name="timeOption"
                                    value="datetime"
                                    checked={timeLimitationType === 'datetime'}
                                    onChange={() => handleTimeLimitationTypeChange('datetime')}
                                    className="mr-2"
                                  />
                                  <span className="text-sm text-gray-700">Specific date/time range</span>
                                </label>
                              </div>

                              {/* Days input - only show when days is selected */}
                              {timeLimitationType === 'days' && (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="number"
                                    value={daysValue}
                                    onChange={(e) => {
                                      const value = e.target.value === '' ? 1 : parseInt(e.target.value) || 1;
                                      setDaysValue(value);
                                    }}
                                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                                    min="1"
                                    step="1"
                                  />
                                  <select
                                    value={daysUnit}
                                    onChange={(e) => setDaysUnit(e.target.value as 'day' | 'week' | 'month' | 'year')}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                                  >
                                    <option value="day">day(s)</option>
                                    <option value="week">week(s)</option>
                                    <option value="month">month(s)</option>
                                    <option value="year">year(s)</option>
                                  </select>
                                  <span className="text-sm text-gray-500">from creation</span>
                                </div>
                              )}

                              {/* Date/Time Pickers - only show when datetime is selected */}
                              {timeLimitationType === 'datetime' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Access From
                                    </label>
                                    <DateTimePicker
                                      value={formData.settings.access_limitations?.allowed_access_from || null}
                                      onChange={(value) => {
                                        if (value) {
                                          updatePermission('general', 'allowed_access_from', value);
                                          const toValue = formData.settings.access_limitations?.allowed_access_to;
                                          validateDatetimeValues(value, toValue);
                                        } else {
                                          updatePermission('general', 'allowed_access_from', undefined);
                                          setDatetimeErrors(prev => ({ ...prev, from: undefined }));
                                        }
                                      }}
                                      showTimeSelect={true}
                                      timeIntervals={1}
                                      dateFormat="yyyy-MM-dd HH:mm"
                                      returnFormat="iso"
                                      className={datetimeErrors.from ? 'border-red-300 bg-red-50' : ''}
                                      popperPlacement="bottom-end"
                                    />
                                    {datetimeErrors.from && (
                                      <p className="mt-1 text-sm text-red-600">{datetimeErrors.from}</p>
                                    )}
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Access To
                                    </label>
                                    <DateTimePicker
                                      value={formData.settings.access_limitations?.allowed_access_to || null}
                                      onChange={(value) => {
                                        if (value) {
                                          updatePermission('general', 'allowed_access_to', value);
                                          const fromValue = formData.settings.access_limitations?.allowed_access_from;
                                          validateDatetimeValues(fromValue, value);
                                        } else {
                                          updatePermission('general', 'allowed_access_to', undefined);
                                          setDatetimeErrors(prev => ({ ...prev, to: undefined }));
                                        }
                                      }}
                                      showTimeSelect={true}
                                      timeIntervals={1}
                                      dateFormat="yyyy-MM-dd HH:mm"
                                      returnFormat="iso"
                                      className={datetimeErrors.to ? 'border-red-300 bg-red-50' : ''}
                                    />
                                    {datetimeErrors.to && (
                                      <p className="mt-1 text-sm text-red-600">{datetimeErrors.to}</p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            )}
                          </div>

                        </div>
                    </div>

                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* File Permissions - Only show for file assets */}
                    {formData.asset_type === 'file' && (
                      <div className="card">
                        <div className="flex items-center space-x-2 mb-4">
                          <DocumentIcon className="w-5 h-5 text-green-600" />
                          <h3 className="text-lg font-semibold text-gray-900">File Permissions</h3>
                        </div>
                        
                        <div className="space-y-4">
                          {[
                            { key: 'convert_original', label: 'Convert to original', icon: ArrowPathIcon },
                            { key: 'save_changes', label: 'Save changes to secured asset', icon: PencilIcon },
                            { key: 'copy_paste', label: 'Copy/Paste', icon: ClipboardDocumentIcon },
                            { key: 'screen_capture', label: 'Screenshot', icon: CameraIcon },
                            { key: 'screen_watermark', label: 'Screen watermark', icon: EyeIcon },
                            { key: 'print', label: 'Print', icon: PrinterIcon },
                            { key: 'print_watermark', label: 'Print watermark', icon: PrinterIcon },
                            { key: 'share', label: 'Share', icon: UserGroupIcon },
                          ]
                          .filter(({ key }) => {
                            // Filter based on company policy settings
                            // For new policies, use current time to check apply_after logic
                            const now = new Date();
                            if (key === 'screen_watermark' || key === 'print_watermark') {
                              return isFilePermissionVisible(key, now);
                            }
                            return isFilePermissionVisible(key, now);
                          })
                          .map(({ key, label, icon: Icon }) => (
                            <div key={key} className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Icon className="w-4 h-4 text-gray-600" />
                                <span className="text-sm font-medium text-gray-700">{label}</span>
                              </div>
                              <ToggleSwitch
                      checked={key === 'screen_watermark'
                        ? formData.settings.screen_watermark
                        : key === 'print_watermark'
                        ? formData.settings.print_watermark
                        : formData.settings.file_permissions?.[key as keyof FilePermissions]?.allowed || false}
                                onChange={(checked) => {
                                  if (key === 'screen_watermark') {
                                    updatePermission('watermark', 'screen_enabled', checked);
                                  } else if (key === 'print_watermark') {
                                    updatePermission('watermark', 'print_enabled', checked);
                                  } else {
                                    updatePermission('file_permissions', key, checked);
                                  }
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Folder Permissions - Only show for folder assets */}
                    {formData.asset_type === 'folder' && (
                      <div className="card">
                        <div className="flex items-center space-x-2 mb-4">
                          <FolderIcon className="w-5 h-5 text-green-600" />
                          <h3 className="text-lg font-semibold text-gray-900">Folder Permissions</h3>
                        </div>
                        
                        <div className="space-y-4">
                          {[
                            { key: 'create', label: 'Create', icon: FolderPlusIcon },
                            { key: 'copy', label: 'Copy', icon: DocumentDuplicateIcon },
                            { key: 'add', label: 'Add', icon: PlusCircleIcon },
                            { key: 'move', label: 'Move', icon: ArrowRightCircleIcon },
                            { key: 'share', label: 'Share', icon: UserGroupIcon },
                            { key: 'delete', label: 'Delete', icon: TrashIcon },
                          ]
                          .filter(({ key }) => {
                            // Filter based on company policy settings
                            // For new policies, use current time to check apply_after logic
                            const now = new Date();
                            return isFolderPermissionVisible(key, now);
                          })
                          .map(({ key, label, icon: Icon }) => (
                            <div key={key} className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Icon className="w-4 h-4 text-gray-600" />
                                <span className="text-sm font-medium text-gray-700">{label}</span>
                              </div>
                              <ToggleSwitch
                                checked={formData.settings.folder_permissions?.[key as keyof ApiFolderPermissions]?.allowed || false}
                                onChange={(checked) => updatePermission('folder_permissions', key, checked)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}



                  </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-between mt-8">
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleSaveDraft}
                      disabled={saveDraftMutation.isPending || savePolicyMutation.isPending}
                      className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saveDraftMutation.isPending ? 'Saving Draft...' : (editingPolicy ? 'Save as Draft' : 'Save as Draft')}
                    </button>
                    <button
                      onClick={() => setStep(3)}
                      className="px-6 py-2 bg-governate-600 text-white rounded-lg hover:bg-governate-700 transition-colors"
                    >
                      Next: Recipients →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Policy Recipients */}
          {step === 3 && (
            <div className="p-6">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Policy Recipients</h2>
                  <button
                    onClick={() => setStep(2)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    ← Back to Permissions
                  </button>
                </div>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <ShieldCheckIcon className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Pre-configure Recipients
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>
                            Add recipients who will have access to files created with this policy. 
                            You can add individual users, entire companies, or custom groups.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Policy Recipients */}
                  <PolicyRecipientSelector
                    selectedRecipients={policyRecipients}
                    onRecipientsChange={setPolicyRecipients}
                  />
                </div>

                {/* Navigation */}
                <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setStep(2)}
                    className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Back to Permissions
                  </button>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleSaveDraft}
                      disabled={saveDraftMutation.isPending || savePolicyMutation.isPending}
                      className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saveDraftMutation.isPending ? 'Saving Draft...' : (editingPolicy ? 'Save as Draft' : 'Save as Draft')}
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={savePolicyMutation.isPending || saveDraftMutation.isPending}
                      className="px-6 py-2 bg-governate-600 text-white rounded-lg hover:bg-governate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {savePolicyMutation.isPending ? 'Saving...' : (editingPolicy ? 'Update Policy' : 'Create Policy')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>


    </div>
  );
};

export default PolicyCreateForm;
